import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '~/components/button';

export const Route = createFileRoute('/_authed/founder/proof')({
  component: ProofLedger,
});

interface ProofRow {
  id: string;
  kind: 'win' | 'authority_signal' | 'milestone' | 'testimonial' | 'capability';
  title?: string;
  evidence?: string;
  source?: string;
  occurredAt: string | null;
  createdAt: string;
}

const KIND_LABEL: Record<string, string> = {
  win: 'win',
  authority_signal: 'authority signal',
  milestone: 'milestone',
  testimonial: 'testimonial',
  capability: 'capability',
};

async function fetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (!r.ok) throw new Error(`${path} -> ${r.status}`);
  return (await r.json()) as T;
}

function ProofLedger() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['proof'], queryFn: () => fetchJson<{ proof: ProofRow[] }>('/api/founder/proof') });

  const [kind, setKind] = useState<ProofRow['kind']>('win');
  const [title, setTitle] = useState('');
  const [evidence, setEvidence] = useState('');
  const [source, setSource] = useState('');
  const [occurredAt, setOccurredAt] = useState('');

  const create = useMutation({
    mutationFn: () =>
      fetchJson<{ id: string }>('/api/founder/proof', {
        method: 'POST',
        body: JSON.stringify({
          kind,
          title: title.trim(),
          evidence: evidence.trim() || undefined,
          source: source.trim() || undefined,
          occurredAt: occurredAt || undefined,
        }),
      }),
    onSuccess: () => {
      setTitle('');
      setEvidence('');
      setSource('');
      setOccurredAt('');
      qc.invalidateQueries({ queryKey: ['proof'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      fetchJson<{ ok: boolean }>(`/api/founder/proof/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proof'] }),
  });

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">proof ledger</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        evidence from your own record. when the imposter voice gets loud, this is what the coach points at.
      </p>

      <form
        className="mt-8 rounded-[var(--radius)] border border-border p-4 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!title.trim()) return;
          create.mutate();
        }}
      >
        <div className="flex flex-wrap gap-1">
          {(Object.keys(KIND_LABEL) as Array<ProofRow['kind']>).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`text-xs px-2 py-1 rounded-full border ${kind === k ? 'border-foreground bg-foreground text-background' : 'border-border'}`}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="title — short. 'shipped phase 3 to 30 users.' 'keynote at conf.' 'first $5k month.'"
          className="w-full h-11 rounded-[var(--radius)] border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <textarea
          value={evidence}
          onChange={(e) => setEvidence(e.target.value)}
          rows={2}
          placeholder="evidence — the concrete bit. a number, a quote, a screenshot URL. (optional)"
          className="w-full rounded-[var(--radius)] border border-input bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <div className="flex gap-2">
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="source (optional)"
            className="flex-1 h-9 rounded-[var(--radius)] border border-input bg-background px-3 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <input
            type="date"
            value={occurredAt}
            onChange={(e) => setOccurredAt(e.target.value)}
            className="h-9 rounded-[var(--radius)] border border-input bg-background px-3 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <Button type="submit" size="sm" disabled={!title.trim() || create.isPending}>
            {create.isPending ? 'saving…' : 'add proof'}
          </Button>
        </div>
      </form>

      <ul className="mt-8 space-y-3">
        {(list.data?.proof ?? []).map((p) => (
          <li key={p.id} className="rounded-[var(--radius)] border border-border p-3 text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <div className="font-medium">{p.title ?? '(no title)'}</div>
              <div className="text-xs text-muted-foreground">
                {KIND_LABEL[p.kind]}
                {p.occurredAt ? ` · ${new Date(p.occurredAt).toLocaleDateString()}` : ''}
              </div>
            </div>
            {p.evidence && (
              <p className="mt-2 text-muted-foreground whitespace-pre-wrap leading-relaxed">{p.evidence}</p>
            )}
            {p.source && <p className="mt-1 text-xs text-muted-foreground">source: {p.source}</p>}
            <div className="mt-2">
              <button
                onClick={() => remove.mutate(p.id)}
                className="text-xs text-muted-foreground hover:text-destructive underline"
              >
                remove
              </button>
            </div>
          </li>
        ))}
        {(list.data?.proof ?? []).length === 0 && (
          <li className="text-sm text-muted-foreground">
            nothing yet. add one win, one capability, one moment someone called you out for being good at something.
          </li>
        )}
      </ul>
    </section>
  );
}
